import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gem, Package, ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { KineticHeading } from "@/components/motion/KineticHeading";

const SECTIONS = [
  {
    icon: Gem,
    title: "Bespoke gold jewellery",
    tag: "All bangles · non-returnable",
    body: [
      "Every bangle is handmade to order to your personal specifications — including your wrist sizing and, on selected pieces, your own horse's hair. Because each piece is created uniquely for you, all bespoke gold jewellery is non-returnable and non-refundable.",
      "Your design and exact measurements are confirmed with you before any crafting begins, so there are no surprises. If you have any questions before ordering, please reach out and we'll guide you personally.",
    ],
  },
  {
    icon: Package,
    title: "Candle & cushion",
    tag: "Returnable within 14 days",
    body: [
      "Our lifestyle items — the Signature Candle and the Tally Ho Cushion — may be returned within 14 days of delivery, provided they are unopened, unused and in their original, new condition with all packaging intact.",
      "To arrange a return, contact us within the 14-day window and we'll share the return details. Items that have been opened, used or are not in resalable condition cannot be accepted.",
    ],
  },
  {
    icon: ShieldAlert,
    title: "Damaged or faulty items",
    tag: "We'll make it right",
    body: [
      "If your order arrives damaged or faulty, please contact us within 48 hours of delivery with photographs of the item and packaging. We'll arrange a repair or replacement as appropriate — this applies to all products, including bespoke pieces.",
    ],
  },
];

export default function Returns() {
  return (
    <div className="pt-36 pb-28 px-6 md:px-12 lg:px-24 max-w-[1100px] mx-auto" data-testid="returns-page">
      <Seo title="Returns & Refunds" description="Equine Evolve returns & refunds policy — bespoke gold jewellery is made to order and non-returnable; candles and cushions can be returned within 14 days if unopened and unused." />
      <Reveal>
        <p className="ee-overline mb-4">Policy</p>
        <KineticHeading as="h1" text="Returns & Refunds" className="font-display text-4xl md:text-6xl tracking-tight flex flex-wrap" stagger={0.05} />
        <p className="text-[#A8A29E] mt-6 max-w-2xl leading-relaxed">
          We want you to love your Equine Evolve pieces. Because much of what we make is created uniquely
          for you, please read the policy below before ordering.
        </p>
      </Reveal>

      <div className="mt-14 space-y-6">
        {SECTIONS.map((s) => (
          <Reveal key={s.title}>
            <div className="border ee-hairline bg-[#1C1917] p-8 md:p-10" data-testid={`returns-${s.title.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-start gap-5">
                <span className="flex-shrink-0 h-12 w-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                  <s.icon size={20} strokeWidth={1.4} />
                </span>
                <div>
                  <p className="ee-overline mb-1 ee-gold-text">{s.tag}</p>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-4">{s.title}</h2>
                  {s.body.map((p, i) => (
                    <p key={i} className="text-[#A8A29E] leading-relaxed text-sm md:text-base mb-3 last:mb-0">{p}</p>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-14 border ee-hairline bg-[#0C0A09] p-10 text-center">
          <p className="ee-overline mb-3">Need to arrange a return or have a question?</p>
          <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-6">We'll look after you personally.</h3>
          <Link to="/contact-us" data-testid="returns-contact-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0C0A09] px-9 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors">
            Contact us <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
